import pako from 'pako';

import { CURRENT_SCHEMA_VERSION } from './schema';

/**
 * Client-side route sharing. A route is deflated + base64url-encoded into a
 * `?r=` query param on /route-planner. No backend, no DB — the recipient's
 * client re-derives geometry from Mapbox Directions, so only the small
 * structural fields travel in the URL.
 */

// Decompression-bomb guard. A real route (≤12 waypoints, ≤5 stops) encodes to
// ~1–2 KB, so an 8 KB ceiling on the encoded param leaves generous headroom
// while bounding the inflate (a single deflate stream expands at most ~1032×,
// i.e. ≤ ~8 MB) before we ever build a string or call JSON.parse. Anything
// larger is rejected as malformed, so a hand-crafted `?r=` can't freeze the tab.
export const MAX_ENCODED_PARAM_LENGTH = 8000;

const toBase64Url = (uint8) => {
  let binary = '';
  for (let i = 0; i < uint8.length; i += 1) {
    binary += String.fromCharCode(uint8[i]);
  }
  const b64 =
    typeof btoa !== 'undefined'
      ? btoa(binary)
      : Buffer.from(binary, 'binary').toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const fromBase64Url = (str) => {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const binary =
    typeof atob !== 'undefined'
      ? atob(b64)
      : Buffer.from(b64, 'base64').toString('binary');
  const uint8 = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    uint8[i] = binary.charCodeAt(i);
  }
  return uint8;
};

/**
 * Encode the shareable subset of a route into a URL-safe param. Drops `id`
 * and timestamps — those are minted fresh if the recipient saves the route.
 *
 * @param {any} route
 * @returns {string}
 */
export const encodeRouteToParam = (route) => {
  const payload = {
    schemaVersion: route.schemaVersion || CURRENT_SCHEMA_VERSION,
    name: route.name || '',
    corridorWidthKm: route.corridorWidthKm,
    routePreferences: route.routePreferences,
    waypoints: route.waypoints,
    itinerary: route.itinerary,
  };
  const deflated = pako.deflate(JSON.stringify(payload));
  return toBase64Url(deflated);
};

/**
 * Decode a `?r=` param back into a raw route object. Returns null on any
 * malformed input so the caller can fall back gracefully.
 *
 * @param {any} param
 * @returns {object | null}
 */
export const decodeRouteFromParam = (param) => {
  if (!param || typeof param !== 'string') return null;
  if (param.length > MAX_ENCODED_PARAM_LENGTH) return null;
  try {
    const json = pako.inflate(fromBase64Url(param), { to: 'string' });
    const obj = JSON.parse(json);
    return obj && typeof obj === 'object' ? obj : null;
  } catch {
    return null;
  }
};

/**
 * Build the full shareable URL for a route.
 *
 * @param {any} route
 * @param {string} [origin]
 * @returns {string}
 */
export const buildShareUrl = (route, origin) => {
  const base =
    origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/route-planner?r=${encodeRouteToParam(route)}`;
};
